import { ref } from 'vue';

export function useCopyFunctionality(datetime, cycleChartData) {
  const copySuccess = ref(false);
  const lastMouseEvent = ref(null);
  const lastHoveredPoints = ref(null);

  // Helper function for timezone formatting
  function formatTimezone(dateString, partnerView) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  }

  function addCopyEventListener() {
    this.handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'c') {
        copyTooltipData();
      }
    };

    document.addEventListener('keydown', this.handleKeyDown);
  }

  function removeCopyEventListener() {
    if (this.handleKeyDown) {
      document.removeEventListener('keydown', this.handleKeyDown);
    }
  }

  function copyTooltipData() {
    if (!this.$refs.chart || !this.$refs.chart.chart) {
      return;
    }

    const chart = this.$refs.chart.chart;

    // First try to get the stored hovered points from mouseMove event
    let hoveredPoints = lastHoveredPoints.value;

    // If no stored points, try to get current hovered points
    if (!hoveredPoints || hoveredPoints.length === 0) {
      hoveredPoints = chart.hoverPoints;
    }

    if (hoveredPoints && hoveredPoints.length > 0) {
      copyDataPointsToClipboard(hoveredPoints);
    } else {
      // Try alternative method - get points from the last mouse position
      const plotX = chart.plotX;
      const plotY = chart.plotY;

      if (plotX !== undefined && plotY !== undefined) {
        // Find the closest points to the mouse position
        const allPoints = [];
        chart.series.forEach(series => {
          if (series.visible) {
            const point = series.searchPoint({ chartX: plotX, chartY: plotY }, true);
            if (point) {
              allPoints.push(point);
            }
          }
        });

        if (allPoints.length > 0) {
          copyDataPointsToClipboard(allPoints);
        } else {
          showCopyError('No data points found. Please hover over a data point first.');
        }
      } else {
        showCopyError('No data points found. Please hover over a data point first.');
      }
    }
  }

  function copyDataPointsToClipboard(points) {
    if (!points || points.length === 0) return;

    // Get the timestamp from the first point
    const pointIndex = points[0].x;

    // Get the actual datetime string from the datetime array
    let formattedTime = 'Unknown Time';
    if (datetime.value && datetime.value[pointIndex]) {
      formattedTime = datetime.value[pointIndex];
    } else if (cycleChartData.value?.datetime && cycleChartData.value.datetime[pointIndex]) {
      // Fallback to raw datetime data and format it
      formattedTime = formatTimezone(cycleChartData.value.datetime[pointIndex], false);
    }

    // Build the data string
    let dataString = `Chart Data - ${formattedTime}\n`;
    dataString += '='.repeat(50) + '\n';

    // Add each data point
    points.forEach(point => {
      if (point.y !== null && point.y !== undefined) {
        const seriesName = point.series.name;
        let value = point.y;
        let unit = '';

        // Add appropriate units
        if (seriesName === 'Humidity') {
          unit = '%';
        } else if (seriesName === 'Pressure') {
          unit = ' Pa';
        } else if (seriesName.includes('Temp') || seriesName.includes('Temperature')) {
          unit = '°F';
        }

        dataString += `${seriesName}: ${value}${unit}\n`;
      }
    });

    // Add Delta T if both temperatures are available
    const returnTemp = points.find(p => p.series.name === 'Return T');
    const supplyTemp = points.find(p => p.series.name === 'Supply T');

    if (returnTemp && supplyTemp && returnTemp.y !== null && supplyTemp.y !== null) {
      const deltaT = returnTemp.y - supplyTemp.y;
      dataString += `Delta T: ${deltaT.toFixed(1)}°F\n`;
    }

    dataString += '='.repeat(50);

    // Copy to clipboard
    copyToClipboard(dataString);
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      showCopySuccess();
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      fallbackCopyToClipboard(text);
    }
  }

  function fallbackCopyToClipboard(text) {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      document.execCommand('copy');
      showCopySuccess();
    } catch (err) {
      console.error('Fallback copy failed:', err);
    }

    document.body.removeChild(textArea);
  }

  function showCopySuccess() {
    // Create a temporary success message
    const successMsg = document.createElement('div');
    successMsg.textContent = 'Data copied to clipboard!';
    successMsg.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-family: Arial, sans-serif;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `;

    document.body.appendChild(successMsg);

    // Remove after 2 seconds
    setTimeout(() => {
      if (successMsg.parentNode) {
        successMsg.parentNode.removeChild(successMsg);
      }
    }, 2000);
  }

  function showCopyError(message) {
    // Create a temporary error message
    const errorMsg = document.createElement('div');
    errorMsg.textContent = message;
    errorMsg.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #dc3545;
      color: white;
      padding: 12px 20px;
      border-radius: 4px;
      z-index: 10000;
      font-family: Arial, sans-serif;
      font-size: 14px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    `;

    document.body.appendChild(errorMsg);

    // Remove after 3 seconds
    setTimeout(() => {
      if (errorMsg.parentNode) {
        errorMsg.parentNode.removeChild(errorMsg);
      }
    }, 3000);
  }

  return {
    copySuccess,
    lastMouseEvent,
    lastHoveredPoints,
    addCopyEventListener,
    removeCopyEventListener,
    copyTooltipData,
    copyDataPointsToClipboard,
    copyToClipboard,
    showCopySuccess,
    showCopyError
  };
}
